<script setup>
import { defineProps, defineEmits, computed, toRef } from 'vue';
import { useIntegrationHook } from 'dashboard/composables/useIntegrationHook';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  integrationId: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['add', 'delete']);
const router = useRouter();
const { t } = useI18n();

// Use toRef so computed inside the composable stays reactive to prop changes
const integrationIdRef = toRef(props, 'integrationId');
const { integration, hasConnectedHooks } = useIntegrationHook(integrationIdRef);

const integrationIconMap = {
  webhook: 'i-ph-webhooks-logo-bold',
  dashboard_apps: 'i-ph-squares-four-bold',
  openai: 'i-ph-brain-bold',
  dialogflow: 'i-ph-robot-bold',
  google_translate: 'i-ph-translate-bold',
  dyte: 'i-ph-video-camera-bold',
  leadsquared: 'i-ph-chart-line-up-bold',
  slack: 'i-ph-slack-logo-bold',
  linear: 'i-ph-kanban-bold',
  shopify: 'i-ph-shopping-bag-bold',
  notion: 'i-ph-note-bold',
  captain: 'i-ph-sparkle-bold',
};

const integrationIcon = computed(() => {
  return integrationIconMap[props.integrationId] || 'i-ph-puzzle-piece-bold';
});

const goBack = () => {
  router.push({ name: 'settings_applications' });
};
</script>

<template>
  <div
    class="single-integration-page flex flex-col w-full h-full overflow-hidden"
  >
    <!-- Header -->
    <header
      class="ws-header flex items-center justify-between px-6 flex-shrink-0"
      style="min-height: 56px"
    >
      <div class="flex items-center gap-3">
        <button class="back-btn" @click="goBack">
          <span class="i-ph-arrow-left-bold size-4" />
          Back
        </button>
        <span style="color: #d1d5db">|</span>
        <div class="flex items-center gap-2">
          <span
            :class="integrationIcon"
            class="size-5"
            style="color: #111827"
          />
          <h1 class="text-lg font-semibold" style="color: #111827">
            {{ integration.name }}
          </h1>
        </div>
      </div>
      <div>
        <button
          v-if="hasConnectedHooks"
          class="disconnect-btn"
          @click="emit('delete', integration.hooks[0])"
        >
          <span class="i-ph-plug-bold size-4" />
          {{ t('INTEGRATION_APPS.DISCONNECT.BUTTON_TEXT') }}
        </button>
        <button v-else class="connect-btn" @click="emit('add')">
          <span class="i-ph-plug-bold size-4" />
          {{ t('INTEGRATION_APPS.CONNECT.BUTTON_TEXT') }}
        </button>
      </div>
    </header>

    <!-- Description -->
    <div class="ws-desc px-6 py-3">
      <p class="text-sm" style="color: #6b7280">
        {{ integration.description }}
      </p>
    </div>

    <!-- Stats -->
    <div class="ws-desc px-6 py-3">
      <div class="flex items-center gap-2">
        <div v-if="hasConnectedHooks" class="status-badge connected">
          <span class="i-ph-check-circle-bold size-4" />
          <span>Connected</span>
        </div>
        <div v-else class="status-badge disconnected">
          <span class="i-ph-x-circle-bold size-4" />
          <span>Not connected</span>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto p-6">
      <!-- Connected State -->
      <div v-if="hasConnectedHooks" class="integration-card">
        <div class="flex items-center gap-4">
          <div class="integration-icon-wrap">
            <span
              :class="integrationIcon"
              class="size-7"
              style="color: #111827"
            />
          </div>
          <div class="flex flex-col justify-center flex-1 min-w-0">
            <h3 class="text-base font-semibold mb-1" style="color: #111827">
              {{ integration.name }}
            </h3>
            <p class="text-sm" style="color: #6b7280">
              Integration is active and connected to your account
            </p>
          </div>
        </div>
      </div>

      <!-- Not Connected State -->
      <div
        v-else
        class="flex flex-col items-center justify-center py-20 text-center"
      >
        <div class="ws-empty-icon">
          <span
            :class="integrationIcon"
            class="size-10"
            style="color: #9ca3af"
          />
        </div>
        <h3 class="text-lg font-semibold mb-2" style="color: #111827">
          Connect {{ integration.name }}
        </h3>
        <p class="text-sm mb-6 max-w-md" style="color: #6b7280">
          {{ integration.description }}
        </p>
        <button class="connect-btn" @click="emit('add')">
          <span class="i-ph-plug-bold size-4" />
          {{ t('INTEGRATION_APPS.CONNECT.BUTTON_TEXT') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.single-integration-page,
.dark .single-integration-page {
  background: #ffffff !important;
}
.ws-header {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
}
.ws-desc {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}
.back-btn:hover {
  color: #111827;
  background: #f3f4f6;
}

.connect-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 600;
  background: #111827;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
}
.connect-btn:hover {
  background: #1f2937;
}

.disconnect-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 600;
  background: #fef2f2;
  color: #b91c1c;
  border: 1px solid #fecaca;
  border-radius: 8px;
  cursor: pointer;
}
.disconnect-btn:hover {
  background: #fee2e2;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 9999px;
}
.status-badge.connected {
  background: #ecfdf5;
  color: #059669;
  border: 1px solid #a7f3d0;
}
.status-badge.disconnected {
  background: #f3f4f6;
  color: #9ca3af;
  border: 1px solid #e5e7eb;
}

.integration-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
}
.integration-icon-wrap {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.ws-empty-icon {
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

/* Deep overrides for modals */
:deep(.modal-mask .modal-container) {
  background: #ffffff !important;
  border: 1px solid #e5e7eb !important;
}
:deep(.modal-mask .modal-header),
:deep(.modal-mask .modal-content),
:deep(.modal-mask form) {
  background: #ffffff !important;
}
:deep(.modal-mask label) {
  color: #111827 !important;
}
:deep(.modal-mask input:not(.reset-base)),
:deep(.modal-mask select:not(.reset-base)),
:deep(.modal-mask textarea:not(.reset-base)) {
  background: #ffffff !important;
  border-color: #d1d5db !important;
  color: #111827 !important;
}
:deep(.text-n-slate-12) {
  color: #111827 !important;
}
:deep(.text-n-slate-11) {
  color: #4b5563 !important;
}
</style>
